import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import type { Charger } from '../../api/chargers';
import { useUsableCards, type Card } from '../../api/drivers';
import type { DialogProps } from '../../components/dialogs/DialogProvider';
import { FormDialog } from '../../components/dialogs/FormDialog';
import { useCommand } from './useCommand';

/**
 * Starts charging the car plugged into a connector: either unbilled (a one-time tag only
 * this charger accepts), or on a driver's card, so the session is theirs and billed as usual.
 */
export function StartSessionDialog({ charger, connectorId, close }: { charger: Charger; connectorId: number } & DialogProps) {
  const [how, setHow] = useState<'free' | 'card'>('free');
  const [card, setCard] = useState<Card | null>(null);
  const [error, setError] = useState('');
  const cards = useUsableCards(charger.organisationId);
  const { run, busy } = useCommand(charger);

  const submit = async () => {
    setError('');
    if (how === 'card' && !card) return setError('Choose the driver’s card.');
    const r = await run({ action: 'RemoteStartTransaction', connectorId, ...(how === 'card' && card ? { cardId: card.id } : {}) });
    if (r) close();
  };

  return (
    <FormDialog
      title={charger.connectorCount > 1 ? `Start a session on connector ${connectorId}?` : 'Start a session?'}
      lead="The charger starts charging the car that is plugged in."
      submitLabel="Start charging"
      onSubmit={() => void submit()}
      onClose={() => close()}
      busy={busy}
      error={error}
      maxWidth="sm"
    >
      <RadioGroup value={how} onChange={(e) => setHow(e.target.value as 'free' | 'card')} aria-label="Who the session is for">
        <FormControlLabel value="free" control={<Radio />} label={<Box><Typography>Without a card</Typography><Typography variant="body2" color="textSecondary">Recorded as started from Pulsepoint, not billed to anyone.</Typography></Box>} sx={{ alignItems: 'flex-start', mb: 1.5, '& .MuiRadio-root': { mt: -0.75 } }} />
        <FormControlLabel value="card" control={<Radio />} label={<Box><Typography>On a driver’s card</Typography><Typography variant="body2" color="textSecondary">For a driver who forgot their card: the session is theirs and billed as usual.</Typography></Box>} sx={{ alignItems: 'flex-start', '& .MuiRadio-root': { mt: -0.75 } }} />
      </RadioGroup>
      {how === 'card' && (
        <Autocomplete
          options={cards.data ?? []}
          loading={cards.isPending}
          value={card}
          onChange={(_e, v) => setCard(v)}
          getOptionLabel={(c) => `${c.driverName}: ${c.label ? `${c.label}, ` : ''}${c.tag}`}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          noOptionsText={`No working cards in ${charger.organisationName ?? 'this organisation'}`}
          renderInput={(params) => <TextField {...params} label="Driver’s card" autoFocus helperText="Type a name or card number" />}
        />
      )}
    </FormDialog>
  );
}
